import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { Dropdown, usePermanentRealtimeDealers } from "components";
import "modules/ReportTemplatesDashboard/components/ReportTemplatesDashboardModal/ReportTemplatesDashboardModal.scss";
import { useAssignReportTemplateMutation } from "modules/ReportTemplatesDashboard/hooks";

import type { DealerID, ReportTemplate } from "models";

type ReportTemplatesDashboardModalProps = {
  reportTemplate: ReportTemplate;
  onClose: () => void;
};

export const ReportTemplatesDashboardModal = ({ reportTemplate, onClose }: ReportTemplatesDashboardModalProps) => {
  const { t } = useTranslation();

  const [selectedDealerId, setSelectedDealerId] = useState<DealerID | null>(reportTemplate.dealer_id);

  const { dealers } = usePermanentRealtimeDealers();
  const { mutateAsync: assignReportTemplate, isPending } = useAssignReportTemplateMutation();

  const dealerOptions = dealers.map(({ id, name }) => ({ key: id, text: name, value: id }));

  const handleSave = async () => {
    await assignReportTemplate({ id: reportTemplate.id, dealer_id: selectedDealerId });
    onClose();
  };

  return (
    <Modal open onClose={onClose} closeOnDimmerClick={false} size="small" className="ReportTemplatesDashboardModal">
      <Modal.Header className="padding-container">{t("v8_report_template", "Report template")}</Modal.Header>
      <Modal.Content className="padding-container">
        <div className="field">
          <label>{t("v8_name", "Name")}</label>
          <span>{reportTemplate.name}</span>
        </div>
        <div className="field">
          <label>{t("v8_description", "Description")}</label>
          <span>{reportTemplate.description}</span>
        </div>
        <div className="field">
          <label htmlFor="dealer">{t("v8_dealer", "Dealer")}</label>
          <Dropdown
            id="dealer"
            fluid
            search
            selection
            clearable
            placeholder={t("v8_select_dealer", "Select dealer")}
            value={selectedDealerId ?? undefined}
            options={dealerOptions}
            onChange={value => setSelectedDealerId(value ?? null)}
          />
        </div>
      </Modal.Content>

      <Modal.Actions className="padding-container">
        <div className="save-cancel-buttons-container">
          <Button className="light" disabled={isPending} onClick={onClose}>
            {t("v8_cancel", "Cancel")} <Icon className="xmark" />
          </Button>
          <Button color="green" loading={isPending} disabled={isPending} onClick={handleSave}>
            {t("v8_save", "Save")} <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
